<template>
  <div class="w-full flex items-center h-10 relative">
    <canvas ref="canvas" class="w-full h-full"></canvas>

    <div
      v-for="region in regions"
      :key="region.label"
      class="absolute top-[-5px]"
      :style="getRegionStyles(region)"
    >
      <div
        class="h-[50px] border-2 border-y-0 rounded-[3px] mb-0.5"
        :class="{
          'border-x-gray-500 bg-gray-500/10': props.type === 'original',
          ...(props.type === 'infringing' && {
            'border-x-red-500 bg-red-500/10': region.type === 'danger',
            'border-x-yellow-500 bg-yellow-500/10': region.type === 'warning',
            'border-x-green-500 bg-green-500/10': region.type === 'normal',
          }),
        }"
      >
        <div
          class="px-[3px] py-0.5 h-[18px] border border-white rounded-sm text-[11px] leading-[12px] font-medium font-secondary tracking-[-0.07] shdw text-white flex items-center justify-center absolute -top-2 -left-2"
          :class="{
            'bg-gray-500': props.type === 'original',
            ...(props.type === 'infringing' && {
              'bg-red-500': region.type === 'danger',
              'bg-yellow-500': region.type === 'warning',
              'bg-green-500': region.type === 'normal',
            }),
          }"
        >
          {{ props.label }}
        </div>
      </div>

      <div
        class="px-[5px] py-[3px] rounded-[6px] shadow-[0_4px_8px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.05)] font-secondary font-medium text-white text-[11px] leading-[12px] tracking-[-0.07px] w-fit whitespace-nowrap relative -translate-x-1/2 left-1/2 z-10"
        :class="{
          'bg-gray-500': props.type === 'original',
          ...(props.type === 'infringing' && {
            'bg-red-500': region.type === 'danger',
            'bg-yellow-500': region.type === 'warning',
            'bg-green-500': region.type === 'normal',
          }),
        }"
      >
        {{ region.label }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import {
  fetchWaveformData,
  type WaveformStem,
} from '@/features/copyright/utils/waveform'
import { onMounted, onUnmounted, ref, nextTick } from 'vue'

export interface Region {
  id: string | number
  label: string
  start: number
  end: number
  type: 'normal' | 'warning' | 'danger'
}

const props = defineProps<{
  url: string
  regions?: Region[]
  stem?: string
  type?: 'original' | 'infringing'
  level?: ScoreLevel
  label: string | number
}>()

const canvas = ref<HTMLCanvasElement>()
const duration = ref(0)
let resizeObserver: ResizeObserver | null = null
let resizeTimeout: ReturnType<typeof setTimeout> | null = null

const COLORS = {
  darkGray: 'rgba(110, 116, 128, 1.00)',
  gray: 'rgba(175, 181, 192, 1)',
  grayFaded: 'rgba(175, 181, 192, 0.25)',
  red: 'rgba(238, 136, 138, 1)',
  yellow: 'rgba(245, 185, 88)',
  green: 'rgba(96, 204, 214)',
}

const drawWaveform = (amplitudes: WaveformStem[]) => {
  const canvasEl = canvas.value
  if (!canvasEl) return

  const ctx = canvasEl.getContext('2d')
  if (!ctx) return

  const rect = canvasEl.getBoundingClientRect()
  if (!rect.width || !rect.height) return

  const dpr = window.devicePixelRatio || 1
  const W = Math.max(1, Math.round(rect.width * dpr))
  const H = Math.max(1, Math.round(rect.height * dpr))
  canvasEl.width = W
  canvasEl.height = H

  const centerY = Math.round(H / 2)
  const topScale = centerY
  const botScale = H - centerY

  ctx.clearRect(0, 0, W, H)

  const start = props.regions?.[0]?.start || 0
  const end = props.regions?.[0]?.end || 0

  const levelColor =
    props.level === ScoreLevel.High
      ? COLORS.red
      : props.level === ScoreLevel.Medium
        ? COLORS.yellow
        : COLORS.green
  const matchColor = props.type === 'original' ? COLORS.darkGray : levelColor

  for (const amp of amplitudes) {
    const N = amp.max.length
    if (!N) continue
    const isTarget = !props.stem || amp.name === props.stem
    const bucketsPerCol = N / W

    for (let x = 0; x < W; x++) {
      const i0 = Math.min(N - 1, Math.floor(x * bucketsPerCol))
      const i1 = Math.min(
        N,
        Math.max(i0 + 1, Math.floor((x + 1) * bucketsPerCol))
      )

      let mx = 0
      let mn = 0
      for (let i = i0; i < i1; i++) {
        const v = amp.max[i] ?? 0
        if (v > mx) mx = v
        const u = amp.min[i] ?? 0
        if (u < mn) mn = u
      }

      const tp = amp.timepoints[i0] ?? 0
      let color = isTarget ? COLORS.gray : COLORS.grayFaded
      if (isTarget && tp >= start && tp <= end) color = matchColor

      const top = Math.round(mx * topScale)
      const bot = Math.round(-mn * botScale)
      ctx.fillStyle = color
      if (top > 0) ctx.fillRect(x, centerY - top, 1, top)
      if (bot > 0) ctx.fillRect(x, centerY, 1, bot)
    }
  }
}

const MIN_REGION_WIDTH = 6

const getRegionStyles = (region: Region) => {
  const start = (region.start / duration.value) * 100
  const end = (region.end / duration.value) * 100

  return {
    width: `${end - start}%`,
    minWidth: `${MIN_REGION_WIDTH}px`,
    left: `min(${start}%, calc(100% - ${MIN_REGION_WIDTH}px))`,
  }
}

onMounted(async () => {
  const data = await fetchWaveformData(props.url)

  const amplitudes: WaveformStem[] = []
  if (props.stem) {
    const target = data.stems.find((s) => s.name === props.stem)
    if (data.main) amplitudes.push(data.main)
    if (target) amplitudes.push(target)
  } else if (data.main) {
    amplitudes.push(data.main)
  }
  if (!amplitudes.length) return

  duration.value = amplitudes[0].duration

  await nextTick()
  drawWaveform(amplitudes)

  if (canvas.value) {
    resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        drawWaveform(amplitudes)
      }, 100)
    })

    resizeObserver.observe(canvas.value)
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (resizeTimeout) {
    clearTimeout(resizeTimeout)
    resizeTimeout = null
  }
})
</script>
